import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useGameStore } from '../../store/gameStore';
import { AdminProvider, useAdmin } from './AdminContext';
import { REALMS } from '../../../../shared/src/realms';
import './admin.css';

const NAV_ITEMS = [
  { path: 'dashboard', icon: '📊', label: 'DASHBOARD' },
  { path: 'factory', icon: '🏭', label: 'FACTORY' },
  { path: 'database', icon: '📦', label: 'DATABASE' },
  { path: 'review', icon: '✅', label: 'REVIEW', showBadge: true },
  { path: 'coverage', icon: '🗺️', label: 'COVERAGE' },
  { path: 'analytics', icon: '📈', label: 'ANALYTICS' },
  { path: 'settings', icon: '⚙️', label: 'SETTINGS' },
];

function AdminShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const nickname = useGameStore((s) => s.nickname);
  const { totalCount, reviewCount, realmStats } = useAdmin();

  const isAdmin = user?.email === 'kemal@aimtech.ai' || (import.meta.env.DEV && nickname === 'KEMAL');

  /**
   * The dev bypass above opens the panel on nickname alone, without a Supabase
   * session. That is convenient for reading, and quietly broken for writing:
   * with no JWT, row-level security sees an anonymous caller, is_admin() is
   * false, and every update or delete is discarded without an error. The panel
   * then looks like it works while changing nothing, which is exactly how the
   * review queue appeared to lose approvals on refresh.
   *
   * So the panel says it out loud rather than letting the next silent no-op be
   * mistaken for a bug in the code.
   */
  const signedInAsAdmin = user?.email === 'kemal@aimtech.ai';

  if (!isAdmin) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#050505' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'var(--font-heading)', color: '#ef4444', fontSize: '1.5rem' }}>UNAUTHORIZED</h2>
          <button className="admin-btn admin-btn-secondary" onClick={() => navigate('/')}>RETURN TO HUB</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      {!signedInAsAdmin && (
        <div style={{
          background: '#2a0f16', borderBottom: '1px solid #f43f5e55', color: '#f8a3b4',
          padding: '10px 18px', fontSize: '0.9rem', lineHeight: 1.5,
        }}>
          <strong style={{ color: '#ff8fa3' }}>Oturum yok, yazma islemleri calismaz.</strong>{' '}
          Panel dev bypass ile acildi ({user ? `oturum: ${user.email}` : 'hic oturum yok'}).
          Supabase istekleri anon key ile gidiyor, RLS onlari sessizce dusuruyor: onayla, sil ve
          duzenle hicbir sey yapmaz. kemal@aimtech.ai ile giris yap.
        </div>
      )}

      {/* ═══ HEADER ═══ */}
      <header className="admin-header">
        <div className="admin-header-title">
          <div className="admin-logo">🔶</div>
          <h1>INTELLIGENCE OS</h1>
        </div>
        <div className="admin-header-stats">
          <span className="stat-badge">{totalCount.toLocaleString()} Qs</span>
          <span className="stat-badge">{Object.keys(realmStats).length} Realms</span>
          {reviewCount > 0 && <span className="stat-badge" style={{ color: '#ef4444', borderColor: '#ef444433' }}>{reviewCount} unreviewed</span>}
        </div>
        <button className="admin-header-exit" onClick={() => navigate('/')}>✕ EXIT</button>
      </header>

      {/* ═══ SIDEBAR ═══ */}
      <aside className="admin-sidebar">
        <nav className="admin-nav">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={`/sys/wotgate/${item.path}`}
              className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
            >
              <span className="admin-nav-icon">{item.icon}</span>
              <span className="admin-nav-label">{item.label}</span>
              {item.showBadge && reviewCount > 0 && (
                <span className="admin-nav-badge">{reviewCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          {Object.entries(REALMS).slice(0, 5).map(([key, val]) => (
            <div key={key} className="footer-stat">
              <span style={{ color: val.color }}>●</span>
              <span>{key}</span>
              <span className="footer-stat-value">{realmStats[key]?.total || 0}</span>
            </div>
          ))}
          <div className="footer-stat" style={{ color: '#444', marginTop: '4px' }}>
            + {Math.max(0, Object.keys(REALMS).length - 5)} more
          </div>
        </div>
      </aside>

      {/* ═══ CONTENT ═══ */}
      <main className="admin-content">
        <Outlet />
      </main>
    </div>
  );
}

// Wrap with provider
export function AdminLayout() {
  return (
    <AdminProvider>
      <AdminShell />
    </AdminProvider>
  );
}
